import { templateAllowed, templatePlan, type PlanId } from "@/lib/plans/plans";

/** The tags the design gallery filters by (editor.designs.* in the locales). */
export const DESIGN_STYLES = ["minimal", "corporate", "creative", "elegant"] as const;
export const DESIGN_TYPEFACES = ["sans", "serif", "mono"] as const;
export type DesignStyle = (typeof DESIGN_STYLES)[number];
export type DesignTypeface = (typeof DESIGN_TYPEFACES)[number];

/**
 * Invoice templates rendered by services/pdf. Adding one = an EJS folder in
 * services/pdf/assets/views/templates and an entry here. The candidate designs
 * (services/pdf/assets/views/candidates) are not offered. `plan` is the cheapest plan that
 * includes it (lib/plans.ts); the included ones come first. `style` and `typeface` (the title's)
 * are the gallery's filters; its thumbnail is client/public/designs/<id>.webp (services/pdf
 * README, "Thumbnails").
 */
export const INVOICE_TEMPLATES = (
  [
    { id: "montana", name: "Montana", style: "corporate", typeface: "sans" },
    { id: "zurich", name: "Zurich", style: "minimal", typeface: "sans" },
    { id: "sprite", name: "Sprite", style: "minimal", typeface: "sans" },
    { id: "kontor", name: "Kontor", style: "corporate", typeface: "sans" },
    { id: "maison", name: "Maison", style: "elegant", typeface: "sans" },
    { id: "frankfurt", name: "Frankfurt", style: "corporate", typeface: "sans" },
    { id: "headline", name: "Headline", style: "minimal", typeface: "sans" },
    { id: "gallery", name: "Gallery", style: "minimal", typeface: "sans" },
    { id: "folio", name: "Folio", style: "minimal", typeface: "sans" },
    { id: "baltic", name: "Baltic", style: "minimal", typeface: "sans" },
    { id: "courier", name: "Courier", style: "creative", typeface: "mono" },
    { id: "meridian", name: "Meridian", style: "corporate", typeface: "sans" },
    { id: "ledger", name: "Ledger", style: "corporate", typeface: "sans" },
    { id: "riviera", name: "Riviera", style: "minimal", typeface: "sans" },
    { id: "boutique", name: "Boutique", style: "elegant", typeface: "sans" },
    { id: "masthead", name: "Masthead", style: "creative", typeface: "sans" },
    { id: "binder", name: "Binder", style: "creative", typeface: "sans" },
    { id: "tabular", name: "Tabular", style: "corporate", typeface: "sans" },
    { id: "haiku", name: "Haiku", style: "minimal", typeface: "sans" },
    { id: "verso", name: "Verso", style: "elegant", typeface: "serif" },
  ] as const satisfies readonly {
    id: string;
    name: string;
    style: DesignStyle;
    typeface: DesignTypeface;
  }[]
).map((template) => ({ ...template, plan: templatePlan(template.id) }));

export type TemplateId = (typeof INVOICE_TEMPLATES)[number]["id"];

/**
 * Every design's colour, as it prints without a choice (the editor's colour picker starts from
 * it). Mirrors ACCENT_DEFAULTS in services/pdf/src/renderer/templates.ts (its assets test
 * compares both).
 */
export const TEMPLATE_ACCENTS: Record<TemplateId, string> = {
  binder: "#1c1c1c",
  boutique: "#111111",
  courier: "#151619",
  folio: "#1a1a1a",
  frankfurt: "#1a1a1a",
  gallery: "#57524c",
  headline: "#111111",
  kontor: "#1d1d1d",
  ledger: "#262626",
  maison: "#141414",
  montana: "#dddddd",
  riviera: "#1a1a1a",
  sprite: "#141414",
  tabular: "#1c1c1c",
  zurich: "#333333",
  baltic: "#111111",
  meridian: "#1f1f1f",
  masthead: "#141414",
  haiku: "#232323",
  verso: "#26221f",
};

export type DesignFilter = {
  query: string;
  style: DesignStyle | null;
  typeface: DesignTypeface | null;
  /** Only the designs the plan includes. */
  included: boolean;
};

/** The designs the gallery shows for its filters, in the editor's order. */
export function filterTemplates(filter: DesignFilter, plan: PlanId) {
  const query = filter.query.trim().toLowerCase();
  return INVOICE_TEMPLATES.filter(
    (tpl) =>
      (!query || tpl.name.toLowerCase().includes(query)) &&
      (!filter.style || tpl.style === filter.style) &&
      (!filter.typeface || tpl.typeface === filter.typeface) &&
      (!filter.included || templateAllowed(plan, tpl.id)),
  );
}

/** The design's thumbnail (client/public/designs). */
export const templateThumbnail = (id: string) => `/designs/${id}.webp`;
